import React, { useState, useRef, useEffect } from "react";
import { FaRegComment } from "react-icons/fa";
import { FiMoreHorizontal, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import SocialLikeButton from "./SocialLikeButton";
import SocialTagOverlay from "./SocialTagOverlay";
import api from "../api";
import "./style/SocialGallery.css";

const API_BASE = process.env.REACT_APP_MODEL_ENDPOINT || "";

/**
 * Instagram-style card for a single social post.
 *
 * Props:
 *   photo           SocialPhotoData
 *   authenticated   bool
 *   currentUserId   number | null
 *   onLoginRequired () => void
 *   onOpenComments  (photoId) => void
 *   onPhotoUpdate   (photoId, {liked, count}) => void
 *   onDelete        (photoId) => void
 */
export default function SocialPostCard({
  photo,
  authenticated,
  currentUserId,
  onLoginRequired,
  onOpenComments,
  onPhotoUpdate,
  onDelete,
}) {
  const [liked, setLiked] = useState(photo.user_liked);
  const [likeCount, setLikeCount] = useState(photo.like_count);
  const [imgError, setImgError] = useState(false);

  // Options menu
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Inline edit
  const [editing, setEditing] = useState(false);
  const [caption, setCaption] = useState(photo.caption || "");
  const [saving, setSaving] = useState(false);

  const isAuthor = authenticated && currentUserId === photo.author.user_id;

  // Carousel state for multi-image posts
  const images = photo.images || [];
  const isMulti = images.length > 1;
  const [activeIdx, setActiveIdx] = useState(0);
  const slideDir = useRef(1); // 1 = forward (slide from right), -1 = backward (slide from left)
  const touchStartX = useRef(null);

  function goNext() { slideDir.current = 1;  setActiveIdx((i) => i + 1); }
  function goPrev() { slideDir.current = -1; setActiveIdx((i) => i - 1); }

  function handleTouchStart(e) {
    touchStartX.current = e.touches[0].clientX;
  }

  function handleTouchEnd(e) {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return; // ignore taps
    if (delta > 0 && activeIdx < images.length - 1) goNext();
    if (delta < 0 && activeIdx > 0) goPrev();
  }

  // Reset imgError when navigating carousel
  useEffect(() => {
    setImgError(false);
  }, [activeIdx]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [menuOpen]);

  function handleLikeToggled(newLiked, newCount) {
    setLiked(newLiked);
    setLikeCount(newCount);
    onPhotoUpdate?.(photo.social_photo_id, { liked: newLiked, count: newCount });
  }

  async function handleDelete() {
    setMenuOpen(false);
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/social/photo/${photo.social_photo_id}`);
      onDelete?.(photo.social_photo_id);
    } catch {}
  }

  function handleEditClick() {
    setMenuOpen(false);
    setEditing(true);
  }

  async function handleSaveCaption() {
    setSaving(true);
    try {
      await api.patch(`/social/photo/${photo.social_photo_id}`, { caption });
    } catch {}
    setSaving(false);
    setEditing(false);
  }

  function handleCancelEdit() {
    setCaption(photo.caption || "");
    setEditing(false);
  }

  function formatDate(dateStr) {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function initials(name) {
    if (!name) return "?";
    return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
  }

  const thumbUrl = `${API_BASE}/social/img?pid=${photo.social_photo_id}&thumb=true`;

  return (
    <div className="social-post-card">
      {/* Header */}
      <div className="social-post-card__header">
        <div className="social-post-card__avatar-initials">
          {initials(photo.author.display_name)}
        </div>
        <span className="social-post-card__author">{photo.author.display_name}</span>
        {photo.is_shared && (
          <span className="social-post-card__shared-badge">Shared from gallery</span>
        )}
        <span className="social-post-card__date">{formatDate(photo.created_at)}</span>

        {isAuthor && (
          <div className="social-post-card__menu-wrapper" ref={menuRef}>
            <button
              className="social-post-card__menu-btn"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Post options"
            >
              <FiMoreHorizontal size={18} />
            </button>
            {menuOpen && (
              <div className="social-post-card__menu">
                <button className="social-post-card__menu-item" onClick={handleEditClick}>
                  Edit
                </button>
                <button className="social-post-card__menu-item social-post-card__menu-item--danger" onClick={handleDelete}>
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image + tag overlay */}
      <div className="social-post-card__image-wrapper">
        {isMulti ? (
          <div
            className="social-post-card__carousel"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {activeIdx > 0 && (
              <button
                className="social-post-card__carousel-arrow social-post-card__carousel-arrow--left"
                onClick={goPrev}
                aria-label="Previous image"
              >
                <FiChevronLeft size={18} strokeWidth={2.5} />
              </button>
            )}

            {!imgError ? (
              <img
                key={activeIdx}
                src={`${API_BASE}/social/img?pid=${photo.social_photo_id}&iid=${images[activeIdx].post_image_id}&thumb=true`}
                className={`social-post-card__image social-post-card__image--slide${slideDir.current === 1 ? "-right" : "-left"}`}
                alt={caption || `Photo ${activeIdx + 1}`}
                onError={() => setImgError(true)}
                loading="lazy"
              />
            ) : (
              <div className="social-post-card__img-error">Image unavailable</div>
            )}

            {activeIdx < images.length - 1 && (
              <button
                className="social-post-card__carousel-arrow social-post-card__carousel-arrow--right"
                onClick={goNext}
                aria-label="Next image"
              >
                <FiChevronRight size={18} strokeWidth={2.5} />
              </button>
            )}

            <div className="social-post-card__dots">
              {images.map((_, i) => (
                <span
                  key={i}
                  className={`social-post-card__dot${i === activeIdx ? " social-post-card__dot--active" : ""}`}
                  onClick={() => setActiveIdx(i)}
                />
              ))}
            </div>

            {activeIdx === 0 && <SocialTagOverlay tags={photo.tags} />}
          </div>
        ) : (
          <>
            {!imgError ? (
              <img
                src={
                  images[0]
                    ? `${API_BASE}/social/img?pid=${photo.social_photo_id}&iid=${images[0].post_image_id}&thumb=true`
                    : thumbUrl
                }
                className="social-post-card__image"
                alt={caption || "Social photo"}
                onError={() => setImgError(true)}
                loading="lazy"
              />
            ) : (
              <div className="social-post-card__img-error">Image unavailable</div>
            )}
            <SocialTagOverlay tags={photo.tags} />
          </>
        )}
      </div>

      {/* Caption — inline editable */}
      {editing ? (
        <div className="social-post-card__edit-caption">
          <textarea
            className="social-post-card__caption-input"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={2}
            autoFocus
          />
          <div className="social-post-card__edit-actions">
            <button className="social-post-card__edit-save" onClick={handleSaveCaption} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button className="social-post-card__edit-cancel" onClick={handleCancelEdit}>
              Cancel
            </button>
          </div>
        </div>
      ) : caption ? (
        <p className="social-post-card__caption">
          <strong>{photo.author.display_name}</strong>{" "}
          {caption}
        </p>
      ) : null}

      {/* Actions */}
      <div className="social-post-card__actions">
        <SocialLikeButton
          photoId={photo.social_photo_id}
          liked={liked}
          count={likeCount}
          authenticated={authenticated}
          onLoginRequired={onLoginRequired}
          onToggled={handleLikeToggled}
        />

        <button
          className="social-action-btn"
          onClick={() => authenticated ? onOpenComments(photo.social_photo_id) : onLoginRequired?.()}
          aria-label="Comments"
        >
          <FaRegComment size={18} />
          {photo.comment_count > 0 && (
            <span className="social-action-btn__count">{photo.comment_count}</span>
          )}
        </button>
      </div>
    </div>
  );
}
